'use client';

/**
 * Global error boundary — catches React render errors that aren't caught
 * by any nested error boundary (e.g. errors in the root layout).
 *
 * Required by the Sentry Next.js integration to capture client-side
 * render errors. Without this file, those errors only show as a generic
 * "An error occurred" page and never reach Sentry.
 *
 * NOTE: this file MUST be at the App Router root (app/global-error.tsx)
 * and MUST include the html/body tags — it replaces the entire document
 * tree when an error happens, so the normal layout isn't applied.
 */

import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="nl">
      <body
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          background: '#fafaf9',
          color: '#0f1115',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: 0,
        }}
      >
        <div
          style={{
            maxWidth: 480,
            padding: 32,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
            Er ging iets mis
          </h1>
          <p style={{ color: '#4a4d55', marginBottom: 24, lineHeight: 1.5 }}>
            Onze excuses voor het ongemak. Het probleem is automatisch
            doorgegeven aan onze ontwikkelaars. Probeer de pagina opnieuw te
            laden.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#0d7c66',
              color: '#fff',
              border: 0,
              padding: '10px 20px',
              borderRadius: 8,
              fontSize: 14,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Pagina opnieuw laden
          </button>
          {error.digest && (
            <div
              style={{
                marginTop: 16,
                fontSize: 11,
                color: '#8a8d95',
                fontFamily: 'monospace',
              }}
            >
              Error ID: {error.digest}
            </div>
          )}
        </div>
      </body>
    </html>
  );
}
